'use client';

import { useQuery } from '@tanstack/react-query';
import lottie from 'lottie-web';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useRef, useState } from 'react';
import { Typography } from '@bgzt/ui';

import { goToFullWebviewOnApp } from '~/services/app-interface';
import { useEnvironmentInfo, useSendLog } from '~/hooks';
import { bannersOptions } from '~/app/search/_queries/banners-options';
import { CONTENT_TYPE, EVENT_ACTION, EVENT_TYPE, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { Image, LogImpression, Section, Spacing } from '~/components';

import styles from './banner.module.css';

export const Banner = () => {
  const { isWebview } = useEnvironmentInfo();
  const sendLog = useSendLog();
  const { data: bannerData } = useQuery(bannersOptions({ device: 'm', inventoryId: 'mercari_home_top' }));
  // prettier-ignore
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 15 }, [
    Autoplay({ stopOnInteraction: false, delay: 3000 }),
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleBannerClick = (webUrl: string = '', index: number) => {
    sendLog({
      event_type: EVENT_TYPE.SELECT_BANNER,
      event_action: EVENT_ACTION.SELECT_BANNER,
      page_id: PAGE_ID.HOME,
      view_id: VIEW_ID.TOP_BANNER,
      content_type: CONTENT_TYPE.BANNER,
      content_position: index,
      url: goToFullWebviewOnApp({ url: bannerData?.[index].webUrl ?? '' }).to,
    });

    if (isWebview) {
      goToFullWebviewOnApp({ url: webUrl }).go();

      return;
    }

    globalThis.window.open(webUrl, '_blank');
  };

  emblaApi?.on('select', () => {
    setCurrentIndex(emblaApi.selectedScrollSnap());
  });

  if (!bannerData?.length) {
    return null;
  }

  return (
    <>
      <Section>
        <div className={clsx(styles.emblaContainer)} ref={emblaRef}>
          <div className={styles.bannerContainer}>
            {bannerData?.map(({ imageUrl }, index) => (
              <div
                key={`${imageUrl}-${index}`}
                className={clsx(styles.bannerSlide)}
                onClick={() => handleBannerClick(bannerData?.[index].webUrl ?? '', index)}
              >
                <LogImpression
                  params={{
                    event_type: EVENT_TYPE.EXPOSURE,
                    event_action: EVENT_ACTION.EXPOSURE_BANNER,
                    page_id: PAGE_ID.HOME,
                    view_id: VIEW_ID.TOP_BANNER,
                    content_type: CONTENT_TYPE.BANNER,
                    content_position: index,
                    url: goToFullWebviewOnApp({ url: bannerData?.[index].webUrl ?? '' }).to,
                  }}
                >
                  <BannerElement imageUrl={imageUrl} isLottie={checkIsLottieFileFormat(imageUrl)} />
                </LogImpression>
              </div>
            ))}
          </div>
          <Fraction totalSlides={bannerData.length} currentIndex={currentIndex} />
        </div>
      </Section>
      <Spacing height={30} />
    </>
  );
};

const checkIsLottieFileFormat = (url: string) => {
  const regex = /(\.){1}json$/g;

  return regex.test(url);
};

const BannerElement = ({ imageUrl, isLottie }: { imageUrl: string; isLottie: boolean }) => {
  const bannerLottieContainer = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!imageUrl || !isLottie) return;

    const animation = lottie.loadAnimation({
      container: bannerLottieContainer.current as HTMLDivElement,
      loop: true,
      autoplay: true,
      path: imageUrl,
      // path: 'https://static.bunjang.co.kr/cms/1odNUeGJiYmopqRMkYjBtuK2yCyZ6fN7D.json',
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    return () => {
      animation?.destroy();
    };
  }, [imageUrl, isLottie]);

  return (
    <div className={styles.bannerElement}>
      {isLottie ? (
        <div ref={bannerLottieContainer} className={styles.lottieContainer} />
      ) : (
        <Image src={imageUrl} alt="탑 배너" width={335} height={98} unoptimized priority />
      )}
    </div>
  );
};

const Fraction = ({ totalSlides, currentIndex }: { totalSlides: number; currentIndex: number }) => {
  return (
    <div className={styles.currentIndexContainer}>
      <div className={styles.currentIndexIndicator}>
        <Typography variant="L11" color="white">
          {currentIndex + 1}
        </Typography>
        <Typography variant="L11" color="white">
          /
        </Typography>
        <Typography variant="L11" color="white">
          {totalSlides}
        </Typography>
      </div>
    </div>
  );
};
